import React, { useEffect, useState, useRef } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, entradaPdfUrl } from "@/lib/api";
import { Plus, Loader2, Search, Warehouse as WarehouseIcon, Download, FileText, LogOut, Pencil, Trash2, Tag, X, Hash, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const inputCls = "w-full bg-background border border-input rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-freight";
const label = "text-[10px] font-bold uppercase tracking-wider text-muted-foreground";

export const BASIS = [
  { k: "dia", l: "Fija €/día" },
  { k: "bulto", l: "€/día · bulto" },
  { k: "m3", l: "€/día · m³" },
  { k: "palet", l: "€/día · palet" },
  { k: "kg", l: "€/kg/día" },
  { k: "semana", l: "€/semana" },
  { k: "kg_semana", l: "€/kg/semana" },
];
const basisLabel = (k) => (BASIS.find((b) => b.k === k) || BASIS[0]).l;

const emptyForm = () => ({
  client_id: "", entry_number: "", entry_date: new Date().toISOString().slice(0, 10),
  origin_company: "", origin_country: "", dest_company: "", dest_country: "",
  packages: "", weight: "", volume_m3: "", pallets: "",
  measures: [{ packages: "", length: "", width: "", height: "", v3: "" }],
  docs_fra: false, docs_dgd: false, un3481: false, handling_hours: "",
  observations: "", storage_rate_per_day: "", storage_basis: "dia", free_days: "", volumetric_factor: "", tariff_id: "",
});

export default function Warehouse() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companySel, setCompanySel] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [tariffOpen, setTariffOpen] = useState(false);
  const [tmOpen, setTmOpen] = useState(false);
  const [tmNext, setTmNext] = useState("");
  const [tmInput, setTmInput] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);
  const [exitFor, setExitFor] = useState(null);
  const [exitForm, setExitForm] = useState({ exit_date: new Date().toISOString().slice(0, 10), packages: "", retired_by_name: "", retired_by_dni: "", retired_by_plate: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [e, t, c] = await Promise.all([
        api.get("/warehouse"),
        api.get("/warehouse/tariffs"),
        api.get("/users?role=client"),
      ]);
      setEntries(e.data); setTariffs(t.data); setClients(c.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const companies = [...new Set(clients.map((c) => (c.company || "").trim() || "Sin empresa"))].sort();
  const operativos = clients.filter((c) => ((c.company || "").trim() || "Sin empresa") === companySel);

  const ql = q.toLowerCase();
  const filtered = entries.filter((e) => {
    const mStatus = statusFilter === "all" || e.status === statusFilter;
    const mQ = !q || [e.entry_number, e.client_name, e.client_company, e.origin_company, e.dest_company]
      .some((v) => (v || "").toLowerCase().includes(ql));
    return mStatus && mQ;
  });

  const openNew = () => { setEditId(null); setCompanySel(""); setForm(emptyForm()); setOpen(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setCompanySel(e.client_company || (e.client_name ? "Sin empresa" : ""));
    setForm({
      client_id: e.client_id || "", entry_number: e.entry_number || "", entry_date: e.entry_date || "",
      origin_company: e.origin_company || "", origin_country: e.origin_country || "",
      dest_company: e.dest_company || "", dest_country: e.dest_country || "",
      packages: e.packages ?? "", weight: e.weight ?? "", volume_m3: e.volume_m3 ?? "", pallets: e.pallets ?? "",
      measures: (Array.isArray(e.measures) && e.measures.length ? e.measures.map((m) => ({ ...m })) : [{ packages: "", length: "", width: "", height: "", v3: "" }]),
      docs_fra: !!e.docs_fra, docs_dgd: !!e.docs_dgd, un3481: !!e.un3481, handling_hours: e.handling_hours ?? "",
      observations: e.observations || "", storage_rate_per_day: e.storage_rate_per_day ?? "", storage_basis: e.storage_basis || "dia", free_days: e.free_days ?? "", volumetric_factor: e.volumetric_factor ?? "", tariff_id: e.tariff_id || "",
    });
    setOpen(true);
  };

  const openTm = async () => {
    try { const { data } = await api.get("/warehouse/tm-counter"); setTmNext(data.next_number); setTmInput(data.next_number); setTmOpen(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveTm = async () => {
    try { const { data } = await api.put("/warehouse/tm-counter", { next_number: tmInput }); setTmNext(data.next_number); toast.success(`Próxima entrada: ${data.next_number}`); setTmOpen(false); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onAiFile = async (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/warehouse/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({
        ...prev,
        origin_company: data.origin_company || prev.origin_company,
        origin_country: data.origin_country || prev.origin_country,
        dest_company: data.dest_company || prev.dest_company,
        dest_country: data.dest_country || prev.dest_country,
        packages: data.packages || prev.packages,
        weight: data.weight || prev.weight,
        volume_m3: data.volume_m3 || prev.volume_m3,
        measures: (data.measures && data.measures.length) ? data.measures : prev.measures,
        observations: data.observations || prev.observations,
      }));
      toast.success("Campos autocompletados con IA");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el documento"); }
    finally { setAiBusy(false); }
  };

  const applyTariff = (id) => {
    const t = tariffs.find((x) => x.id === id);
    if (t) setForm((f) => ({ ...f, tariff_id: id, storage_rate_per_day: t.rate, storage_basis: t.basis || "dia", volumetric_factor: t.volumetric_factor ?? "" }));
    else setForm((f) => ({ ...f, tariff_id: "" }));
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Selecciona empresa y operativo"); return; }
    setBusy(true);
    try {
      const payload = {
        client_id: form.client_id, entry_number: form.entry_number || null, entry_date: form.entry_date || null,
        origin_company: form.origin_company || null, origin_country: form.origin_country || null,
        dest_company: form.dest_company || null, dest_country: form.dest_country || null,
        packages: form.packages !== "" ? parseInt(form.packages) : null,
        weight: form.weight !== "" ? parseFloat(form.weight) : null,
        volume_m3: form.volume_m3 !== "" ? parseFloat(form.volume_m3) : null,
        pallets: form.pallets !== "" ? parseInt(form.pallets) : null,
        measures: form.measures.filter((m) => m.packages || m.length || m.width || m.height || m.v3),
        docs_fra: form.docs_fra, docs_dgd: form.docs_dgd, un3481: form.un3481,
        handling_hours: form.handling_hours !== "" ? parseFloat(form.handling_hours) : null,
        observations: form.observations || null,
        storage_rate_per_day: form.storage_rate_per_day !== "" ? parseFloat(form.storage_rate_per_day) : 0,
        storage_basis: form.storage_basis || "dia", free_days: form.free_days !== "" ? parseInt(form.free_days) : 0,
        volumetric_factor: form.volumetric_factor !== "" ? parseInt(form.volumetric_factor) : null, tariff_id: form.tariff_id || null,
      };
      if (editId) { await api.put(`/warehouse/${editId}`, payload); toast.success("Entrada actualizada"); }
      else { await api.post("/warehouse", payload); toast.success("Entrada registrada"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openExit = (e) => { setExitFor(e); setExitForm({ exit_date: new Date().toISOString().slice(0, 10), packages: e.packages_remaining ?? e.packages ?? "", retired_by_name: "", retired_by_dni: "", retired_by_plate: "" }); };
  const confirmExit = async () => {
    setBusy(true);
    try {
      await api.post(`/warehouse/${exitFor.id}/exit`, {
        exit_date: exitForm.exit_date, retired_by_name: exitForm.retired_by_name,
        retired_by_dni: exitForm.retired_by_dni, retired_by_plate: exitForm.retired_by_plate,
        packages: exitForm.packages !== "" ? parseInt(exitForm.packages) : null,
      });
      toast.success("Salida registrada"); setExitFor(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta entrada de almacén?")) return;
    try { await api.delete(`/warehouse/${id}`); toast.success("Entrada eliminada"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const undoExit = async (id) => {
    if (!window.confirm("¿Anular la última salida y devolver la mercancía al stock?")) return;
    try { await api.post(`/warehouse/${id}/undo-exit`); toast.success("Salida anulada · mercancía de nuevo en stock"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportCSV = () => {
    if (filtered.length === 0) { toast.error("No hay entradas para exportar"); return; }
    const headers = ["Nº Entrada", "Cliente", "Empresa", "F. entrada", "F. salida", "Estado", "Bultos", "Kilos", "Volumen (m3)", "Días en stock", "Días libres", "Días facturables", "Tarifa", "Base", "Coste almacenaje (EUR)", "Observaciones"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((e) => [
      e.entry_number, e.client_name, e.client_company, e.entry_date, e.exit_date || "",
      e.status === "in_stock" ? "En stock" : "Salida", e.packages ?? "", e.weight ?? "", e.volume_m3 ?? "",
      e.days_in_stock ?? "", e.free_days ?? "", e.chargeable_days ?? "", e.storage_rate_per_day ?? "", basisLabel(e.storage_basis), e.storage_cost ?? "", e.observations || "",
    ].map(esc).join(","));
    const blob = new Blob(["\uFEFF" + [headers.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `almacen_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    toast.success(`${filtered.length} entrada(s) exportada(s)`);
  };

  const exportXLSX = async () => {
    if (filtered.length === 0) { toast.error("No hay entradas para exportar"); return; }
    try {
      const res = await api.post("/warehouse/export.xlsx", { ids: filtered.map((e) => e.id) }, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = `almacen_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      toast.success("Excel generado");
    } catch { toast.error("No se pudo generar el Excel"); }
  };

  const totalCost = filtered.reduce((a, e) => a + (e.storage_cost || 0), 0);

  return (
    <Layout>
      <PageHeader title="Almacén" subtitle={`${entries.length} entrada(s)`}
        action={
          <div className="flex gap-2">
            <button data-testid="wh-numbering-btn" onClick={openTm}
              className="px-3 py-2 text-xs font-bold uppercase border border-corp text-corp hover:bg-corp hover:text-white transition-colors flex items-center gap-1.5"><Hash className="w-4 h-4" /> Numeración</button>
            <button data-testid="manage-tariffs-btn" onClick={() => setTariffOpen(true)}
              className="px-3 py-2 text-xs font-bold uppercase border border-corp text-corp hover:bg-corp hover:text-white transition-colors flex items-center gap-1.5"><Tag className="w-4 h-4" /> Tarifas</button>
            <button data-testid="new-entry-btn" onClick={openNew}
              className="px-4 py-2 bg-freight text-black font-bold uppercase text-sm flex items-center gap-2 hover:bg-freight/90 transition-colors"><Plus className="w-4 h-4" /> Nueva entrada</button>
          </div>
        } />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-freight" /></div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="search-warehouse" className="w-full bg-background border border-input rounded-none pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-freight"
                placeholder="Buscar Nº, cliente, empresa u origen" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <select data-testid="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-xs font-bold uppercase border border-input bg-card text-muted-foreground focus:outline-none focus:ring-2 focus:ring-freight">
              <option value="all">Todas</option>
              <option value="in_stock">En stock</option>
              <option value="out">Salidas</option>
            </select>
            <button data-testid="export-csv-btn" onClick={exportCSV}
              className="ml-auto px-3 py-2 text-xs font-bold uppercase border border-corp text-corp hover:bg-corp hover:text-white transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> CSV</button>
            <button data-testid="export-xlsx-btn" onClick={exportXLSX}
              className="px-3 py-2 text-xs font-bold uppercase border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Excel</button>
          </div>

          <div className="border border-border overflow-x-auto bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-3 px-4 font-bold">Nº Entrada</th>
                  <th className="py-3 px-4 font-bold">Empresa</th>
                  <th className="py-3 px-4 font-bold hidden md:table-cell">F. entrada</th>
                  <th className="py-3 px-4 font-bold hidden lg:table-cell">F. salida</th>
                  <th className="py-3 px-4 font-bold hidden sm:table-cell">Bultos/Kg</th>
                  <th className="py-3 px-4 font-bold">Días</th>
                  <th className="py-3 px-4 font-bold">Almacenaje</th>
                  <th className="py-3 px-4 font-bold">Estado</th>
                  <th className="py-3 px-4 font-bold text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">No hay entradas.</td></tr>
                ) : filtered.map((e) => (
                  <tr key={e.id} data-testid={`wh-row-${e.id}`} className="border-b border-border last:border-0 hover:bg-freight/5 transition-colors">
                    <td className="py-3 px-4 font-mono font-bold">{e.entry_number}</td>
                    <td className="py-3 px-4"><div className="font-semibold">{e.client_company || "—"}</div><div className="text-xs text-muted-foreground">{e.client_name}</div></td>
                    <td className="py-3 px-4 hidden md:table-cell text-muted-foreground font-mono text-xs">{e.entry_date}</td>
                    <td className="py-3 px-4 hidden lg:table-cell font-mono text-xs" data-testid={`wh-exitdate-${e.id}`}>{e.exit_date ? <span className="text-freight font-bold">{e.exit_date}</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-3 px-4 hidden sm:table-cell text-muted-foreground">{e.packages_remaining ?? e.packages ?? "—"}{e.packages != null && e.packages_out > 0 ? ` / ${e.packages}` : ""} / {e.weight ?? "—"} kg</td>
                    <td className="py-3 px-4 font-semibold">{e.days_in_stock}</td>
                    <td className="py-3 px-4">
                      <span data-testid={`wh-cost-${e.id}`} className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{(e.storage_cost ?? 0).toFixed(2)} €</span>
                    </td>
                    <td className="py-3 px-4">
                      {e.display_status === "out"
                        ? <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">Salida</span>
                        : e.display_status === "partial"
                          ? <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">Parcial</span>
                          : <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300">En stock</span>}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <a data-testid={`wh-pdf-${e.id}`} href={entradaPdfUrl(e.id)} target="_blank" rel="noreferrer" className="text-corp hover:text-corp-hover inline-flex align-middle mr-3" title="Ver entrada PDF"><FileText className="w-4 h-4" /></a>
                      {e.display_status !== "out" && (
                        <button data-testid={`wh-exit-${e.id}`} onClick={() => openExit(e)} className="text-freight hover:opacity-80 inline-flex align-middle mr-3" title="Registrar salida"><LogOut className="w-4 h-4" /></button>
                      )}
                      {e.packages_out > 0 && (
                        <button data-testid={`wh-undo-${e.id}`} onClick={() => undoExit(e.id)} className="text-amber-600 hover:text-amber-700 inline-flex align-middle mr-3" title="Anular última salida"><RotateCcw className="w-4 h-4" /></button>
                      )}
                      <button data-testid={`wh-edit-${e.id}`} onClick={() => openEdit(e)} className="text-corp hover:text-corp-hover inline-flex align-middle mr-3" title="Editar"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`wh-delete-${e.id}`} onClick={() => remove(e.id)} className="text-red-500 hover:text-red-600 inline-flex align-middle" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 0 && (
              <div className="px-4 py-3 border-t border-border flex justify-end text-sm font-bold" data-testid="wh-total">
                Total almacenaje ({filtered.length}): <span className="ml-2 text-emerald-700 dark:text-emerald-400">{totalCost.toFixed(2)} €</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialog nueva/editar entrada */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><WarehouseIcon className="w-5 h-5 text-freight" /> {editId ? "Editar entrada" : "Nueva entrada en almacén"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
            <div className="flex items-center justify-between gap-3 bg-freight/5 border border-freight/30 p-2">
              <div className="flex-1">
                <label className={label}>Nº de entrada (dejar vacío = automático)</label>
                <input data-testid="entry-number" className={inputCls} value={form.entry_number} placeholder="TM26...." onChange={(e) => setForm({ ...form, entry_number: e.target.value })} />
              </div>
              <div>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={onAiFile} data-testid="entry-ai-input" />
                <button type="button" data-testid="entry-ai-btn" onClick={() => fileRef.current?.click()} disabled={aiBusy}
                  className="mt-4 px-3 py-2 bg-corp hover:bg-corp-hover text-white font-bold uppercase text-xs flex items-center gap-1.5 disabled:opacity-60">
                  {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Adjuntar entrada (IA)
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Cliente (empresa) *</label>
                <select data-testid="entry-company" className={inputCls} value={companySel} onChange={(e) => { setCompanySel(e.target.value); setForm({ ...form, client_id: "" }); }}>
                  <option value="">Seleccionar empresa *</option>
                  {companies.map((co) => <option key={co} value={co}>{co}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Operativo *</label>
                <select data-testid="entry-operativo" className={inputCls} value={form.client_id} disabled={!companySel} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                  <option value="">Seleccionar operativo *</option>
                  {operativos.map((o) => <option key={o.id} value={o.id}>{o.name}{o.email ? ` (${o.email})` : ""}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Fecha de entrada</label><input data-testid="entry-date" type="date" className={inputCls} value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} /></div>
              <div><label className={label}>Horas de manipulación</label><input type="number" step="0.5" className={inputCls} value={form.handling_hours} onChange={(e) => setForm({ ...form, handling_hours: e.target.value })} /></div>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
              <div><label className={label}>Origen · Empresa</label><input className={inputCls} value={form.origin_company} onChange={(e) => setForm({ ...form, origin_company: e.target.value })} /></div>
              <div><label className={label}>Origen · País</label><input className={inputCls} value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} /></div>
              <div><label className={label}>Destino · Empresa</label><input className={inputCls} value={form.dest_company} onChange={(e) => setForm({ ...form, dest_company: e.target.value })} /></div>
              <div><label className={label}>Destino · País</label><input className={inputCls} value={form.dest_country} onChange={(e) => setForm({ ...form, dest_country: e.target.value })} /></div>
            </div>

            <div className="grid grid-cols-4 gap-3 border-t border-border pt-3">
              <div><label className={label}>Bultos</label><input data-testid="entry-packages" type="number" className={inputCls} value={form.packages} onChange={(e) => setForm({ ...form, packages: e.target.value })} /></div>
              <div><label className={label}>Kilos</label><input type="number" step="0.01" className={inputCls} value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></div>
              <div><label className={label}>Volumen (m³)</label><input type="number" step="0.001" className={inputCls} value={form.volume_m3} onChange={(e) => setForm({ ...form, volume_m3: e.target.value })} /></div>
              <div><label className={label}>Palets</label><input type="number" className={inputCls} value={form.pallets} onChange={(e) => setForm({ ...form, pallets: e.target.value })} /></div>
            </div>

            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className={label}>Medidas</label>
                <button type="button" onClick={() => setForm({ ...form, measures: [...form.measures, { packages: "", length: "", width: "", height: "", v3: "" }] })} className="text-xs font-bold uppercase text-corp hover:underline">+ Añadir fila</button>
              </div>
              {form.measures.map((m, i) => (
                <div key={i} className="grid grid-cols-5 gap-2 mb-2">
                  {["packages", "length", "width", "height", "v3"].map((k) => (
                    <input key={k} className={inputCls} placeholder={{ packages: "Bultos", length: "Largo", width: "Ancho", height: "Alto", v3: "V3" }[k]} value={m[k] || ""}
                      onChange={(e) => { const ms = form.measures.map((x, j) => j === i ? { ...x, [k]: e.target.value } : x); setForm({ ...form, measures: ms }); }} />
                  ))}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 border-t border-border pt-3">
              <label className="flex items-center gap-2 text-sm font-semibold"><input data-testid="entry-fra" type="checkbox" checked={form.docs_fra} onChange={(e) => setForm({ ...form, docs_fra: e.target.checked })} /> Acompaña FRA</label>
              <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={form.docs_dgd} onChange={(e) => setForm({ ...form, docs_dgd: e.target.checked })} /> Acompaña DGD</label>
              <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={form.un3481} onChange={(e) => setForm({ ...form, un3481: e.target.checked })} /> UN 3481</label>
            </div>

            <div className="border-t border-border pt-3">
              <p className="text-[10px] font-bold uppercase text-freight mb-2">Almacenaje (facturación)</p>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className={label}>Tarifa guardada</label>
                  <select data-testid="entry-tariff" className={inputCls} value={form.tariff_id} onChange={(e) => applyTariff(e.target.value)}>
                    <option value="">— Manual —</option>
                    {tariffs.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.rate} € · {basisLabel(t.basis)})</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Tarifa (€)</label>
                  <input data-testid="entry-rate" type="number" step="0.01" className={inputCls} value={form.storage_rate_per_day} onChange={(e) => setForm({ ...form, storage_rate_per_day: e.target.value, tariff_id: "" })} />
                </div>
                <div>
                  <label className={label}>Base de cálculo</label>
                  <select data-testid="entry-basis" className={inputCls} value={form.storage_basis} onChange={(e) => setForm({ ...form, storage_basis: e.target.value, tariff_id: "" })}>
                    {BASIS.map((b) => <option key={b.k} value={b.k}>{b.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Días libres</label>
                  <input data-testid="entry-free-days" type="number" min="0" className={inputCls} value={form.free_days} onChange={(e) => setForm({ ...form, free_days: e.target.value })} placeholder="0" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Los días libres se descuentan de los días de estancia antes de calcular el coste.</p>
              <div className="mt-3">
                <label className={label}>Conversión volumen → kg (peso tasable)</label>
                <select data-testid="entry-vfactor" className={inputCls} value={form.volumetric_factor} onChange={(e) => setForm({ ...form, volumetric_factor: e.target.value, tariff_id: "" })}>
                  <option value="">Sin conversión</option>
                  <option value="270">1 m³ = 270 kg</option>
                  <option value="300">1 m³ = 300 kg</option>
                  <option value="333">1 m³ = 333 kg</option>
                </select>
                <p className="text-[10px] text-muted-foreground mt-1">Para tarifas por kg se factura el mayor entre el peso real y el volumétrico (volumen × factor).</p>
              </div>
            </div>

            <div><label className={label}>Observaciones</label><textarea rows={2} className={inputCls} value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button data-testid="save-entry-btn" onClick={save} disabled={busy} className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {editId ? "Guardar cambios" : "Registrar entrada"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog salida */}
      <Dialog open={!!exitFor} onOpenChange={(v) => !v && setExitFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Registrar salida · {exitFor?.entry_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {exitFor && (
              <p className="text-xs text-muted-foreground">En stock: <span className="font-bold text-foreground">{exitFor.packages_remaining ?? exitFor.packages ?? 0}</span> bulto(s). Indica cuántos salen (menos = salida parcial).</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Fecha de salida</label><input data-testid="exit-date" type="date" className={inputCls} value={exitForm.exit_date} onChange={(e) => setExitForm({ ...exitForm, exit_date: e.target.value })} /></div>
              <div><label className={label}>Bultos que salen</label><input data-testid="exit-packages" type="number" min="1" className={inputCls} value={exitForm.packages} onChange={(e) => setExitForm({ ...exitForm, packages: e.target.value })} /></div>
            </div>
            <div><label className={label}>Retirado por · Nombre</label><input data-testid="exit-name" className={inputCls} value={exitForm.retired_by_name} onChange={(e) => setExitForm({ ...exitForm, retired_by_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>D.N.I.</label><input className={inputCls} value={exitForm.retired_by_dni} onChange={(e) => setExitForm({ ...exitForm, retired_by_dni: e.target.value })} /></div>
              <div><label className={label}>Matrícula</label><input className={inputCls} value={exitForm.retired_by_plate} onChange={(e) => setExitForm({ ...exitForm, retired_by_plate: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="confirm-exit-btn" onClick={confirmExit} disabled={busy} className="bg-freight text-black font-bold uppercase text-sm px-5 py-2 flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar salida</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog tarifas */}
      <TariffManager open={tariffOpen} onOpenChange={setTariffOpen} tariffs={tariffs} reload={load} />

      {/* Dialog numeración TM */}
      <Dialog open={tmOpen} onOpenChange={setTmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><Hash className="w-5 h-5 text-corp" /> Numeración de entradas</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Próxima entrada: <span className="font-mono font-bold text-freight">{tmNext}</span></p>
            <div><label className={label}>Fijar próximo número (formato TM26XXXX)</label><input data-testid="tm-input" className={inputCls} value={tmInput} onChange={(e) => setTmInput(e.target.value)} placeholder="TM260586" /></div>
          </div>
          <DialogFooter>
            <button data-testid="save-tm-btn" onClick={saveTm} className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-5 py-2">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function TariffManager({ open, onOpenChange, tariffs, reload }) {
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [basis, setBasis] = useState("dia");
  const [vfactor, setVfactor] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim() || rate === "") { toast.error("Indica nombre y tarifa"); return; }
    setBusy(true);
    try { await api.post("/warehouse/tariffs", { name: name.trim(), rate: parseFloat(rate), basis, volumetric_factor: vfactor !== "" ? parseInt(vfactor) : null }); toast.success("Tarifa guardada"); setName(""); setRate(""); setBasis("dia"); setVfactor(""); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/warehouse/tariffs/${id}`); toast.success("Tarifa eliminada"); reload(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><Tag className="w-5 h-5 text-corp" /> Tarifas de almacenaje</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div><label className={label}>Nombre</label><input data-testid="tariff-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Estándar" /></div>
            <div><label className={label}>Tarifa (€)</label><input data-testid="tariff-rate" type="number" step="0.01" className={inputCls} value={rate} onChange={(e) => setRate(e.target.value)} /></div>
            <div><label className={label}>Base</label><select data-testid="tariff-basis" className={inputCls} value={basis} onChange={(e) => setBasis(e.target.value)}>{BASIS.map((b) => <option key={b.k} value={b.k}>{b.l}</option>)}</select></div>
            <div><label className={label}>Conversión vol.→kg</label><select data-testid="tariff-vfactor" className={inputCls} value={vfactor} onChange={(e) => setVfactor(e.target.value)}><option value="">Sin conversión</option><option value="270">1 m³ = 270 kg</option><option value="300">1 m³ = 300 kg</option><option value="333">1 m³ = 333 kg</option></select></div>
          </div>
          <button data-testid="add-tariff-btn" onClick={add} disabled={busy} className="w-full bg-corp hover:bg-corp-hover text-white font-bold uppercase text-xs px-3 py-2 flex items-center justify-center gap-1 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Guardar tarifa</button>
          <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
            {tariffs.length === 0 ? <div className="p-4 text-center text-sm text-muted-foreground">Sin tarifas guardadas.</div> :
              tariffs.map((t) => (
                <div key={t.id} data-testid={`tariff-row-${t.id}`} className="flex items-center justify-between px-4 py-2.5 text-sm gap-2">
                  <span className="font-semibold flex-1">{t.name}</span>
                  <span className="text-muted-foreground text-xs">{t.rate} € · {basisLabel(t.basis)}{t.volumetric_factor ? ` · ${t.volumetric_factor}kg/m³` : ""}</span>
                  <button data-testid={`delete-tariff-${t.id}`} onClick={() => del(t.id)} className="text-red-500 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
